import { capitalize } from "es-toolkit";

import { getNodeLabels } from "@/components/expression/preview/utils";
import { getSuggestionsDoc } from "@/components/suggestions/suggestionsDoc";
import { insertedNodeType } from "@/components/suggestions/utils/libraryItem";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";
import { suggestionToNodeJson } from "./utils/suggestionToNodeJson";
import { symbolLookupFor } from "./utils/symbolLookup";

import type {
  NewCursor,
  SingleSuggestion,
  SuggestionItem,
  SuggestionNodeType,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { NodeModel } from "@/models/nodes/types";

type NodeModelOfType<T extends SuggestionNodeType> = T extends NodeModel["type"]
  ? NodeModel<T>
  : never;

export function getSuggestionNode<T extends SuggestionNodeType>(
  suggestion: SingleSuggestion<T>,
  doc: DocumentModel | undefined,
): [NodeModelOfType<T>, NewCursor | false | undefined] | undefined {
  const json = suggestionToNodeJson(suggestion);
  const existingNode = suggestion.item.node;

  if (!json) {
    return undefined;
  }

  const d = doc ?? getSuggestionsDoc();
  const node = createNodeFrom(json, d, { resetIds: true });

  if (!node) {
    return undefined;
  }

  let newCursor: NewCursor | false | undefined;

  // Set cursor position based on node type
  switch (insertedNodeType(suggestion.item)) {
    case "variable": {
      const varNode = node as VariableNodeModel;
      if (!existingNode && varNode.hasParams && varNode.parameters.length > 0) {
        newCursor = { node: varNode.parameters[0], index: 0 };
      }
      break;
    }

    case "function": {
      const funcNode = node as FunctionNodeModel;
      // Check if symbol has empty label first
      const firstLabel = funcNode.symbolNode?.labelNodes.find(
        (l) => l.inlineNode && l.inlineNode.nodes.length === 0,
      );

      if (firstLabel?.inlineNode) {
        newCursor = { node: firstLabel.inlineNode, index: 0 };
      } else if (funcNode.parameters.length > 0) {
        newCursor = { node: funcNode.parameters[0], index: 0 };
      }
      break;
    }

    case "operator":
    case "group": {
      // For operators and groups, don't move cursor
      newCursor = false;
      break;
    }

    default:
      // For other node types (constant, symbol, number, etc.), cursor remains undefined
      newCursor = undefined;
      break;
  }

  return [node as NodeModelOfType<T>, newCursor];
}

type CreateSuggestionOptions = {
  priority?: number;
};

// Suggestions for defined variables insert the variable without its unit,
// unless the variable has a power.
function stripVariableUnit<T extends "function" | "constant" | "variable">(
  node: NodeModel<T>,
): NodeModel<T> {
  if (!node.is(VariableNodeModel) || !node.unitNode || node.power) return node;

  const doc = getSuggestionsDoc();
  return doc.transact(
    () => {
      const strippedNode = createNodeFrom(node, doc);
      if (strippedNode.unitNode) {
        doc.removeNode(strippedNode.unitNode);
      }
      return strippedNode as NodeModel<T>;
    },
    "stripVariableUnit",
    { runInAction: true },
  );
}

export function createSuggestionFromNode<T extends "function" | "constant" | "variable">(
  node: NodeModel<T>,
  { priority = 1 }: CreateSuggestionOptions = {},
): SingleSuggestion<T> {
  const nodeType = capitalize(node.type);
  const symbolKey = node.symbolNode?.props.key;
  const symbolKeywords = symbolKey
    ? symbolLookupFor(node.doc).byKey(symbolKey)?.keywords
    : undefined;

  let keywords = normalizeSuggestionKeywords({});
  if (symbolKeywords) {
    keywords = symbolKeywords;
  } else if (node.value) {
    keywords = normalizeSuggestionKeywords({ keywords: [node.value] });
  }

  return {
    type: "single",
    item: {
      type: node.type,
      node: stripVariableUnit(node),
      category: nodeType,
      symbol: node.value ?? "",
      intlName: node.type,
      displayName: node.name,
      keywords,
      labels: getNodeLabels(node), // TODO: check `getNodeLabels` method and there we use `createSuggestionFromNode`
      // because suggestions with labels renders fine without it prop and seems like we can remove `@/components/expression/preview/utils`
      // it only brokes case in this test `useSuggestions.test.ts` - "should return defined variable with a label"
    } as SuggestionItem<T>,
    priority,
  };
}
